import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { cn, toPlainText } from "../lib/helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowCircleUp, faCopy, faTrash, faThumbsUp, faThumbsDown } from "@fortawesome/free-solid-svg-icons";
import Markdown from "markdown-to-jsx";
import { SiteContext } from "./global/site-context";
import { SSE } from "sse.js";
import ReactTooltip from "react-tooltip";
import { Modal } from "./global/modal";

import * as styles from "./global/global.module.css";
import * as pricingStyles from "./sections/snowflake/pricing-plans.module.css";

// https://docs.scoutos.com/api-sdk/endpoints/workflows/run

const BASE_URL = "https://api-prod.scoutos.com";
const POWERED_BY = "Claude, GPT minis, and Gemini";
const POST_TO_SLACK = true;

const BlockType = {
  Thinking: "com.scoutos.copilot_thinking_text",
  Links: "com.scoutos.copilot_list_of_links",
  Message: "com.scoutos.copilot.message",
  Reasoning: "com.scoutos.llm_reasoning",
}

const BlockID = {
  GoalReached: "llm_reasoning_goal",
  Goal: "llm_reasoning_goal_description",
}

// Ensure message is not in chat history
const checkFirstMessage = (msg, messages) => msg && messages.find(m => m.text === msg) === undefined;

export const ScoutCopilot = ({
  className, onResult, style, agentPage,
  copilotID, workflowID, messages, setMessages,
  // current goal index
  goal, setGoal, prompt, setMainGoal, userEmail,
  minimal, agent, showSupport,
  firstMessage, textScroll, whiteBg, skipAutoFocus, stickyForm
}) => {
  const site = useContext(SiteContext);
  const [userId, _setUserId] = useState(Math.random().toString(36).substring(2, 15));
  const [token, setToken] = useState("");
  const [userInput, setUserInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const currentWorkflowRunIdRef = useRef(null);
  const currentSessionIdRef = useRef(null);
  const tokenIntervalRef = useRef(null);
  const wrapperRef = useRef(null);
  const formRef = useRef(null);
  const placeholderRef = useRef(null);
  const anchorRef = useRef(null);
  const [isFormFloating, setIsFormFloating] = useState(false);
  const [gdprHeight, setGdprHeight] = useState(0);
  const formFixedStyleRef = useRef({});
  const messageRef = useRef(null);
  const [streamingMessage, setStreamingMessage] = useState("");
  const [thinkingMessage, setThinkingMessage] = useState("");
  const [currentAchievement, setCurrentAchievement] = useState(null);
  const sseSourceRef = useRef(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [firstMessageSentToAPI, setFirstMessageSentToAPI] = useState(false);
  const [lastSentFirstMessage, setLastSentFirstMessage] = useState(null);
  const isInitialMount = useRef(true);

  const hasSteps = prompt?.steps?.length > 0;
  const experimental = site?.doc?.status === "hidden";

  const isDarkBg = site?.doc?.backgroundColor === "gridBlack";

  const addMessage = ({ text, who = "bot", workflowRunId = null, sessionId = null }) => {
    // Mark that user has interacted when any message is added
    setHasUserInteracted(true);

    console.log("addMessage called with:", { who, workflowRunId, hasWorkflowRunId: !!workflowRunId });

    setMessages(prevMessages => {
      const newMessage = {
        who,
        text,
        datetime: new Date(),
        // Add feedback fields only for bot messages with workflow run ID
        ...(who === "bot" && workflowRunId && {
          workflowRunId,
          sessionId,
          feedback: null,      // null = no feedback, 1 = thumbs up, 0 = thumbs down
          feedbackSent: false  // Track if API call succeeded
        })
      };

      const newMessages = [...prevMessages, newMessage];
      // Save to localStorage with the fresh messages array
      if (site.metrics.analytics.storage) {
        site.metrics.analytics.storage.setItem(
          `chat-history-${agent?._id || "default"}`, JSON.stringify(newMessages)
        );
      }

      return newMessages;
    });
  }

  const updateStreamingMessage = (text) => {
    setStreamingMessage(prev => prev + text);
  }

  const scrollToBottom = () => {
    if (messageRef.current) {
      messageRef.current.scrollTop = messageRef.current.scrollHeight;
    }
  };

  // When stickyForm floats, the user can submit from anywhere on the page
  // and won't otherwise see the response arrive in the chat thread above.
  const scrollToChatAnchor = () => {
    if (!anchorRef.current) return;
    const elementPosition = anchorRef.current.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({ top: elementPosition - 300, behavior: "smooth" });
  };

  // Scroll to bottom when messages change (but not on initial mount)
  // useEffect(() => {
  //   if (isInitialMount.current) {
  //     isInitialMount.current = false;
  //     return;
  //   }
  //   scrollToBottom();
  // }, [messages]);


  useEffect(() => {
    const initializeToken = async () => {
      try {
        const newToken = await fetchToken();
        setToken(newToken);
        console.debug("Initial token fetched");
      } catch (error) {
        console.error("Token initialization failed:", error);
        setError("Failed to initialize connection. Please try again later.");
      }
    };
    initializeToken();

    // token refresh
    tokenIntervalRef.current = setInterval(async () => {
      try {
        if (!token || !isTokenValid(token)) {
          const newToken = await fetchToken();
          setToken(newToken);
          // console.debug("Token refreshed");
        }
      } catch (error) {
        console.error("Token refresh failed:", error);
      }
    }, 4 * 60 * 1000); // every 4 minutes

    return () => {
      if (tokenIntervalRef.current) {
        clearInterval(tokenIntervalRef.current);
      }
      // Clean up SSE connection
      if (sseSourceRef.current) {
        sseSourceRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    const newAchievement = prompt?.steps?.[goal]?.achievement;
    if (newAchievement) {
      setCurrentAchievement(newAchievement);
      site.metrics.logClick("chatbot", "achievement");
    }
  }, [goal]);

  // Reset flag when firstMessage changes (indicates new page context)
  useEffect(() => {
    if (firstMessage && firstMessage !== lastSentFirstMessage) {
      setFirstMessageSentToAPI(false);
    }
  }, [firstMessage, lastSentFirstMessage]);

  const fetchToken = async () => {
    try {
      const response = await fetch(`${BASE_URL}/v2/copilots/${copilotID}/token`, {
        method: "POST",
        headers: {
          "origin": window.location.origin,
        },
      });

      if (!response.ok) {
        throw new Error(`Token fetch failed with status: ${response.status}`);
      }

      const data = await response.json();
      return data.token;
    } catch (error) {
      console.error("Error fetching token:", error);
      throw error;
    }
  };

  const isTokenValid = (tokenToCheck) => {
    try {
      const payload = JSON.parse(atob(tokenToCheck.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return currentTime < payload.exp;
    } catch (error) {
      console.error("Invalid token format:", error);
      return false;
    }
  };

  const executeWorkflow = async (input) => {
    setIsLoading(true);
    setError(null);
    setStreamingMessage("");
    site.metrics.logClick("chatbot", "ask");

    try {
      if (!token || !isTokenValid(token)) {
        const newToken = await fetchToken();
        setToken(newToken);
      }

      // Close any existing SSE connection
      if (sseSourceRef.current) {
        sseSourceRef.current.close();
      }

      // Build chat history from current messages state
      const chatHistory = messages.map(m => ({
        role: m.who === "user" ? "user" : "assistant",
        content: m.text,
      }));

      // Add the current user input to chat history (state hasn't updated yet)
      chatHistory.push({
        role: "user",
        content: input,
      });

      // Insert firstMessage right before the last user message (first API call only)
      if (firstMessage && !firstMessageSentToAPI) {
        chatHistory.splice(chatHistory.length - 1, 0, {
          role: "assistant",
          content: firstMessage,
        });
      }

      const source = new SSE(`${BASE_URL}/v2/workflows/${workflowID}/execute?copilot_id=${copilotID}`, {
        headers: {
          method: "POST",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        payload: JSON.stringify({
          streaming: true,
          environment: "production",
          inputs: {
            user_message: input,
            chat_history: chatHistory,
            metadata: {
              user_id: userId,
              email: userEmail,
              role: prompt?.role,
              tasks: prompt?.tasks,
              interject: prompt?.interject,
              answers: prompt?.answers,
              goal:
                hasSteps
                  ? prompt.steps?.[goal]?.goal
                  : prompt.tasks,
              nextGoal: hasSteps ? prompt.steps?.[goal + 1]?.goal : "",
              smarts: hasSteps ? prompt.steps?.[goal]?.smarts : "",
              response: hasSteps ? prompt.steps?.[goal]?.response : "",
              skipPost: !POST_TO_SLACK,
              url: window.location.hostname + window.location.pathname,
            },
          },
        }),
      });

      sseSourceRef.current = source;

      const handleBlockStateUpdated = (e) => {
        const payload = JSON.parse(e.data);

        if (payload.data.block_type === BlockType.Message) {
          if (payload.data.update_type === "partial") {
            updateStreamingMessage(payload.data.update_data.output);
            setThinkingMessage("");
          }
          // Message complete
          if (payload.data.update_type === "complete") {
            console.log("Message complete, currentWorkflowRunId:", currentWorkflowRunIdRef.current);
            addMessage({
              text: streamingMessage + payload.data.update_data.output,
              workflowRunId: currentWorkflowRunIdRef.current,  // Pass the captured run ID
              sessionId: currentSessionIdRef.current  // Pass the captured session ID
            });
            setStreamingMessage("");
            setIsLoading(false);
            currentWorkflowRunIdRef.current = null; // Reset for next message
            currentSessionIdRef.current = null; // Reset for next message

            // Mark firstMessage as sent after successful API response
            if (firstMessage && !firstMessageSentToAPI) {
              setFirstMessageSentToAPI(true);
              setLastSentFirstMessage(firstMessage);
            }
          }
        }
      };

      const handleBlockRunStarted = (e) => {
        const payload = JSON.parse(e.data);

        // Capture workflow_run_id from payload
        if (payload.data?.state?.__exp_global?.run?.id) {
          currentWorkflowRunIdRef.current = payload.data.state.__exp_global.run.id;
        } else if (payload.data.workflow_run_id) {
          currentWorkflowRunIdRef.current = payload.data.workflow_run_id;
        } else {
          console.log("No workflow run ID found in block_run_started");
        }

        // Capture session_id from payload
        if (payload.data?.state?.__exp_global?.session?.id) {
          currentSessionIdRef.current = payload.data.state.__exp_global.session.id;
        } else if (payload.data.session_id) {
          currentSessionIdRef.current = payload.data.session_id;
        } else {
          console.log("No session ID found in payload");
        }

        return payload;
      };


      const handleBlockRunCompleted = (e) => {

        try {
          const payload = JSON.parse(e.data);
          const blockId = payload.data.block_id;
          const blockType = payload.data.block_type;
          const blockOutput = payload.data.state.output;
          // console.debug("Block Run Completed:", blockType, blockId, blockOutput, payload);

          // goal blocks are done
          if (blockId === BlockID.GoalReached && blockOutput === "true") {
            if (goal < prompt?.steps?.length - 1) {
              setGoal(goal + 1);
              console.debug("GOAL ADVANCED")
            }
          }
          if (blockId === BlockID.Goal) {
            setMainGoal(blockOutput);
          }

          // link extraction blocks are done

          // @ todo Append links to the UI

          // items: blockOutput.links.map((link: object) => Link.parse(link)),
          // header: blockOutput.header,

          switch (blockType) {
            case BlockType.Thinking:
              setThinkingMessage(blockOutput);
              break
            case BlockType.Links:
              break
            default:
          }
        } catch (error) {
          console.error("Error processing block_run_completed event:", error)
        }
      };

      source.addEventListener("block_state_updated", handleBlockStateUpdated);
      source.addEventListener("block_run_started", handleBlockRunStarted);
      source.addEventListener("block_run_completed", handleBlockRunCompleted);

      source.addEventListener("error", function (e) {
        console.error("SSE Error:", e);
        setIsLoading(false);
        setError("Failed to connect. Please try again.");
        if (sseSourceRef.current) {
          sseSourceRef.current.close();
          sseSourceRef.current = null;
        }
      });

      source.addEventListener("open", function (e) {
        // console.debug("SSE connection opened");
      });

      if (onResult && typeof onResult === "function") {
        onResult({ streaming: true });
      }

    } catch (error) {
      console.error("Workflow Execution Error:", error);
      setError("Error. Sorry, please try again.");
      setIsLoading(false);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userInput.trim()) {
      return;
    }

    try {
      if (firstMessage && !firstMessageSentToAPI && messages?.length === 0) {
        addMessage({ text: firstMessage, who: "bot" });
      }
      addMessage({ text: userInput, who: "user" });
      site.metrics.analytics.storage.setItem("chat-path", window.location.pathname);
      setUserInput("");
      if (stickyForm && isFormFloating) {
        scrollToChatAnchor();
      }
      await executeWorkflow(userInput);
    } catch (error) {
      // Error is already set in executeWorkflow
    }
  };

  // Handle key press in the textarea
  const handleKeyDown = (e) => {
    // Check if Enter is pressed without Shift (to allow for newlines with Shift+Enter)
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevent newline insertion
      handleSubmit(e);
    }
  };

  const handleTranscript = (e) => {
    const transcript = messages.map(m =>
      `${m.who === "user" ? "YOU" : "BOT"}: ${m.text}`
    ).join("\n\n");

    if (navigator?.clipboard) {
      navigator.clipboard.writeText(transcript);
    }
    site.metrics.logClick("chatbot", "transcript");
  }

  const handleClearHistory = (e) => {
    setMessages([]);
    setStreamingMessage("");
    setThinkingMessage("");
    setFirstMessageSentToAPI(false);
    setLastSentFirstMessage(null);
    if (agent?._id && site.metrics.analytics.storage) {
      site.metrics.analytics.storage.removeItem(`chat-history-${agent._id}`);
    }

    site.metrics.logClick("chatbot", "clear-history");
  }

  const submitFeedback = useCallback(async (messageIndex, score, comment = "") => {
    const message = messages[messageIndex];

    if (!message.workflowRunId) {
      console.error("Cannot submit feedback: no workflow run ID");
      return false;
    }

    try {
      const response = await fetch(
        `${BASE_URL}/v2/copilots/${copilotID}/feedback`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            target_type: "workflow_run",
            target_id: message.workflowRunId,
            scorer_id: "thumbs",
            score: score, // 1 for thumbs up, 0 for thumbs down
            comment: comment,
            metadata: {
              user_id: userId,
              workflow_id: workflowID,
              session_id: message.sessionId,
              run_id: message.workflowRunId,
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Feedback API failed: ${response.status}`);
      }

      // Update message state
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages];
        updatedMessages[messageIndex] = {
          ...updatedMessages[messageIndex],
          feedback: score,
          feedbackSent: true
        };

        // Persist to localStorage
        if (site.metrics.analytics.storage) {
          site.metrics.analytics.storage.setItem(
            `chat-history-${agent?._id || "default"}`,
            JSON.stringify(updatedMessages)
          );
        }

        return updatedMessages;
      });

      // Log analytics
      site.metrics.logClick(
        "chatbot",
        score === 1 ? "feedback-thumbup" : "feedback-thumbdown"
      );

      return true;
    } catch (error) {
      console.error("Feedback submission error:", error);

      // Still update UI to show feedback was given, but mark as not sent
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages];
        updatedMessages[messageIndex] = {
          ...updatedMessages[messageIndex],
          feedback: score,
          feedbackSent: false
        };
        return updatedMessages;
      });

      return false;
    }
  }, [messages, token, copilotID, userId, workflowID, site.metrics, agent?._id]);

  useEffect(() => {
    if (!stickyForm) return;

    const measureGdpr = () => {
      const el = document.querySelector('.CookieConsent');
      setGdprHeight(el ? el.offsetHeight : 0);
    };

    measureGdpr();
    const observer = new MutationObserver(measureGdpr);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [stickyForm]);

  useEffect(() => {
    if (!stickyForm || !placeholderRef.current || !wrapperRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          const rect = wrapperRef.current.getBoundingClientRect();
          const gdprEl = document.querySelector('.CookieConsent');
          const gdprHeight = gdprEl ? gdprEl.offsetHeight : 0;
          formFixedStyleRef.current = { left: rect.left, width: rect.width, bottom: gdprHeight };
          setIsFormFloating(true);
        } else {
          setIsFormFloating(false);
          formFixedStyleRef.current = {};
        }
      },
      { threshold: 0 }
    );

    observer.observe(placeholderRef.current);
    return () => observer.disconnect();
  }, [stickyForm]);

  return (
    <div
      ref={wrapperRef}
      className={cn("relative flex flex-col justify-end", className)}
      style={style}
    >
      {checkFirstMessage(firstMessage, messages) && messages?.length === 0 &&
        <FirstMessage
          message={firstMessage}
          isDarkBg={isDarkBg}
          whiteBg={whiteBg}
        />
      }
      {messages?.length > 0 && !hasUserInteracted && (
        <p className="text-auxiliary text-xs mb-2">
          <a
            onClick={handleClearHistory}
            className="cursor-pointer hover:text-link hover:underline">
            Clear chat history
          </a>
        </p>
      )}
      {messages?.length > 0 && (
        <div className="relative">
          <div className={cn(
            "absolute top-0 left-0 right-0 h-16 z-10 pointer-events-none bg-gradient-to-b to-transparent",
            isDarkBg ? "from-primary" : whiteBg ? "from-white" : "from-green-ultralight"
          )} />
          <div
            className={cn(
              "absolute bottom-0 left-0 right-0 h-16 z-10 pointer-events-none bg-gradient-to-b from-transparent",
              isDarkBg ? "to-primary" : whiteBg ? "to-white" : "to-green-ultralight"
            )}
            id="fadeOutGradient"
          />
          <div
            ref={messageRef}
            className={cn("py-8 flex flex-col", textScroll ? "overflow-y-scroll lg:max-h-128 pr-6" : null)}
            style={textScroll ? { maxHeight: "36rem" } : null}
          >
            <div className="mt-auto">
              <Messages
                messages={messages}
                agentPage={agentPage}
                streamingMessage={streamingMessage}
                isLoading={isLoading}
                thinkingMessage={thinkingMessage}
                isDarkBg={isDarkBg}
                whiteBg={whiteBg}
                onFeedback={submitFeedback}
              />
            </div>
          </div>
        </div>
      )}
      {prompt.questions?.length > 0 && messages?.length === 0 && (!minimal || textScroll) && (
        <>
          <h4 className="text-auxiliary text-sm mb-2 ml-2 mt-8">Suggested questions</h4>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-2 lg:gap-3 mb-3">
            {prompt.questions.map((m, i) => (
              <li
                key={i}
                className="bg-light-50 hover:text-black p-2 px-3 border border-dark-10 hover:border-green-emerald rounded-xl leading-snug cursor-pointer hover:bg-white hover:text-black transform scale-100 hover:scale-102 transition-card duration-300"
                onClick={async (e) => {
                  // If firstMessage exists and hasn't been sent yet, add it to messages
                  if (firstMessage && !firstMessageSentToAPI && messages?.length === 0) {
                    addMessage({ text: firstMessage, who: "bot" });
                  }
                  addMessage({ text: m.text, who: "user" });
                  await executeWorkflow(m.text);
                  // @todo Use the _rawAnswer if present
                }}
              >
                {m.text}
              </li>
            ))}
          </ul>
        </>
      )}
      <a data-chat-anchor ref={anchorRef} />
      <div ref={placeholderRef} />
      {stickyForm && isFormFloating && (
        <div style={{ height: formRef.current?.offsetHeight }} />
      )}
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className={cn(
          stickyForm && isFormFloating
            ? "fixed bottom-0 z-90 rounded-t-2xl"
            : "sticky bottom-0 z-90 rounded-t-2xl",
          agentPage || isDarkBg || textScroll ? null : "bg-white",
          minimal ? "pb-3" : "pb-6"
        )}
        style={stickyForm ? { ...(isFormFloating ? formFixedStyleRef.current : {}), bottom: gdprHeight } : undefined}
      >
        <div
          className={cn(
            "rounded-3xl overflow-hidden border-4 shadow-lg transition-all duration-300",
            isLoading ? "animate-think border-dark-30" : "border-green-emerald",
            currentAchievement ? "bg-green-emerald" : "bg-white",
          )}>
          {currentAchievement && (
            <a
              href={currentAchievement.url}
              className="px-2 pb-2 pt-1 w-full flex text-white block"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                site.metrics.logClick("chatbot", "achievement-click");
              }}
            >
              <span className="flex-grow">
                {currentAchievement.message}
              </span>
              <span
                className="font-medium transform scale-100 hover:scale-105 transition-size duration-300"
              >
                {currentAchievement.cta}
              </span>
            </a>
          )}
          <div
            className={cn(
              "rounded-t-xl flex",
              isDarkBg ? null : "bg-white",
              stickyForm && isFormFloating ? "px-1" : "p-2 pb-2"
            )}
            style={{ boxShadow: "0 0 15px rgba(0, 0, 0, 0.2)" }}
          >
            <textarea
              id="userInput"
              value={userInput}
              autoFocus={!skipAutoFocus}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={site.isNotMobile && !experimental ? "Ask anything about Common Room" : "Ask anything"}
              className={cn(
                "w-full p-3 bg-white rounded-3xl text-lg font-medium focus:outline-none focus:ring-none overflow-y-auto placeholder:text-dark-50",
                isLoading ? "text-dark-50" : "text-black",
              )}
              style={{
                height: `calc(${(stickyForm && isFormFloating) ? 1 : minimal ? 2 : 4} * 1.75rem + 1.5rem)`,
                transition: 'height 0.3s ease',
              }}
            />
            <button
              disabled={isLoading || !token}
              className={cn(
                "pt-2 mr-2 mb-2 rounded-lg transition-all duration-200 scale-100 transform hover:scale-105 outline-none focus:outline-none",
                isLoading
                  ? "text-dark-30 cursor-not-allowed animate-breath"
                  : "text-green-emerald hover:text-black"
              )}
            >
              <FontAwesomeIcon icon={faArrowCircleUp} size="2x" />
            </button>
          </div>
        </div>
      </form>
      {/* {!minimal && (
        <div className="text-center text-sm">
          <p className="leading-normal mb-2 text-dark-40 hover:text-auxiliary transition-colors duration-300">
            Powered by {POWERED_BY}.
          </p>
        </div>
      )} */}
      <div className="flex justify-center" id="chatControls">
        <button
          onClick={handleTranscript}
          className="p-2 ml-1 text-dark-30 hover:text-black transition-colors outline-none focus:outline-none flex items-center gap-1"
        >
          <FontAwesomeIcon icon={faCopy} className="max-h-8" />
          <span className="text-xs">Copy transcript</span>
        </button>
        <button
          onClick={handleClearHistory}
          className="p-2 ml-1 text-dark-30 hover:text-red transition-colors outline-none focus:outline-none flex items-center gap-1"
          disabled={messages.length === 0}
        >
          <FontAwesomeIcon icon={faTrash} className="max-h-8" />
          <span className="text-xs">Clear history</span>
        </button>
      </div>
      {showSupport && (
        <div className="text-center text-sm mb-5">
          <a
            href="/ask?ref=chatbot-footer"
            className="text-dark-60 hover:text-link transition-colors duration-300">
            Contact Support
          </a>
        </div>
      )}
      {error && (
        <div className="mt-12 px-3 py-2 bg-red/10 border border-red rounded-md text-red">
          {error}
        </div>
      )}
      <ReactTooltip
        id="feedback-thumbup-tooltip"
        effect="solid"
        place="top"
        backgroundColor="#333"
        textColor="white"
        offset={{ top: -5 }}
        className={cn("text-lg", pricingStyles.toolTip)}
      />
      <ReactTooltip
        id="feedback-thumbdown-tooltip"
        effect="solid"
        place="top"
        backgroundColor="#333"
        textColor="white"
        offset={{ top: -5 }}
        className={cn("text-lg", pricingStyles.toolTip)}
      />
    </div>
  );
};

const FeedbackButtons = React.memo(({ message, messageIndex, onFeedback, isDarkBg }) => {
  const site = useContext(SiteContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [selectedScore, setSelectedScore] = useState(null);
  const [feedbackComment, setFeedbackComment] = useState("");

  // If feedback already given, show confirmation
  if (message.feedback !== null && message.feedback !== undefined) {
    return (
      <div className="flex items-center gap-2 ml-2">
        <p className={cn(
          "text-ms mt-2",
          isDarkBg ? "text-light-60" : "text-dark-60"
        )}>
          Thanks for your feedback!
        </p>
        {message.feedback === 1 ? (
          <FontAwesomeIcon
            icon={faThumbsUp}
            className="text-green-emerald"
          />
        ) : (
          <FontAwesomeIcon
            icon={faThumbsDown}
            className="text-red"
          />
        )}
        {!message.feedbackSent && (
          <span className="text-xs text-red">(not sent)</span>
        )}
      </div>
    );
  }

  const handleFeedbackClick = (score) => {
    setSelectedScore(score);
    setShowFeedbackForm(true);
    site.metrics.logClick("chatbot", "feedback-modal-opened");
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await onFeedback(messageIndex, selectedScore, feedbackComment);
    setIsSubmitting(false);
    setShowFeedbackForm(false);
    setFeedbackComment("");
    setSelectedScore(null);

    // Log analytics based on whether comment was provided
    if (feedbackComment.trim()) {
      site.metrics.logClick("chatbot", "feedback-modal-submitted-with-comment");
    } else {
      site.metrics.logClick("chatbot", "feedback-modal-submitted-without-comment");
    }
  };

  const handleModalClose = () => {
    setShowFeedbackForm(false);
    setFeedbackComment("");
    setSelectedScore(null);
    site.metrics.logClick("chatbot", "feedback-modal-dismissed");
  };

  return (
    <>
      <div className="flex items-center gap-2 ml-2 mt-1 mb-2 opacity-50 hover:opacity-100 transition-opacity">
        <span className={cn(
          "text-sm",
          isDarkBg ? "text-light-60" : "text-dark-60"
        )}>
          Was this helpful?
        </span>
        <button
          onClick={() => handleFeedbackClick(1)}
          className={cn(
            "p-1 transition-colors outline-none focus:outline-none",
            "text-dark-40 hover:text-green-emerald hover:scale-110 transform"
          )}
          data-tip="Thumbs up"
          data-for="feedback-thumbup-tooltip"
        >
          <FontAwesomeIcon icon={faThumbsUp} />
        </button>
        <button
          onClick={() => handleFeedbackClick(0)}
          className={cn(
            "p-1 transition-colors outline-none focus:outline-none",
            "text-dark-40 hover:text-red hover:scale-110 transform"
          )}
          data-tip="Thumbs down"
          data-for="feedback-thumbdown-tooltip"
        >
          <FontAwesomeIcon icon={faThumbsDown} />
        </button>
      </div>

      {showFeedbackForm && (
        <Modal setOpen={handleModalClose}>
          <div style={{ minWidth: "28rem" }}>
            <h2 className="text-lg font-medium mb-3">Tell us what you think.</h2>

            <textarea
              value={feedbackComment}
              onChange={(e) => setFeedbackComment(e.target.value)}
              placeholder="Notes optional..."
              rows={5}
              className="w-full border border-dark-20 rounded-lg p-3 focus:outline-none focus:border-green-emerald transition-colors resize-none placeholder:text-dark-40 duration-300"
            />

            <div className="flex items-center justify-between mt-4">
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedScore(1)}
                  className={cn(
                    "p-2 rounded-lg transition-colors outline-none focus:outline-none",
                    selectedScore === 1
                      ? "bg-green-emerald text-white"
                      : "text-dark-40 hover:text-green-emerald"
                  )}
                >
                  <FontAwesomeIcon icon={faThumbsUp} size="lg" />
                </button>
                <button
                  onClick={() => setSelectedScore(0)}
                  className={cn(
                    "p-2 rounded-lg transition-colors outline-none focus:outline-none",
                    selectedScore === 0
                      ? "bg-red text-white"
                      : "text-dark-40 hover:text-red"
                  )}
                >
                  <FontAwesomeIcon icon={faThumbsDown} size="lg" />
                </button>
              </div>

              <div className="flex gap-2">
                {selectedScore === 0 && (
                  <a
                    href={`/ask?ref=chatbot-feedback${feedbackComment.trim() ? `&description=${encodeURIComponent(feedbackComment.trim())}` : ""}`}
                    className="bg-white text-dark-80 border border-dark-20 px-4 py-2 rounded-lg transition-colors hover:bg-dark-5"
                  >
                    Contact Support
                  </a>
                )}
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || selectedScore === null}
                  className={cn(
                    "bg-black text-white px-4 py-2 rounded-lg transition-colors",
                    isSubmitting || selectedScore === null
                      ? "bg-dark-30 cursor-not-allowed"
                      : "hover:bg-dark-80"
                  )}
                >
                  {isSubmitting ? "Sending..." : "Send Feedback"}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
});

const FirstMessage = React.memo(({ message, isDarkBg, whiteBg }) => (
  <div className="flex flex-col justify-end gap-1">
    <p className={cn(
      "text-xs transition-colors duration-300 ml-2",
      isDarkBg ? "text-light-40 hover:text-light-80" : "text-dark-40 hover:text-dark-80"
    )}>
      Chatbot @ now
    </p>
    <div
      className={cn(
        "mb-4 p-3 pb-1.5 rounded-2xl text-xl font-medium sm:max-w-3/4",
        styles.fadeIn,
        isDarkBg ? "bg-light-5" : whiteBg ? "bg-dark-5" : "bg-light-80",
      )}
    >
      <div className={styles.markdown}>
        <p>{message}</p>
      </div>
    </div>
  </div>
));

const Messages = React.memo(({ messages, agentPage, streamingMessage, isLoading, thinkingMessage, isDarkBg, whiteBg, onFeedback }) => {
  const allMessages = [...messages];

  if (thinkingMessage) {
    allMessages.push({
      who: "bot",
      text: thinkingMessage,
      datetime: new Date(),
      isStreaming: true
    });
  }

  if (streamingMessage) {
    allMessages.push({
      who: "bot",
      text: streamingMessage,
      datetime: new Date(),
      isStreaming: true
    });
  }

  return allMessages.length > 0 ? allMessages.map((m, i) => (
    <div key={i} className="flex flex-col justify-end gap-1 mb-4">
      <p className={cn(
        "text-xs transition-colors duration-300",
        m.who === "user" ? "text-right mr-2" : "ml-2",
        isDarkBg ? "text-light-40 hover:text-light-80" : "text-dark-40 hover:text-dark-80"
      )}>
        {m.who === "user" ? "You" : "Chatbot"} @ {(m.datetime instanceof Date ? m.datetime : new Date(m.datetime)).toLocaleTimeString(
          [], { hour: "2-digit", minute: "2-digit" }
        )}
        {m.isStreaming && " streaming..."}
      </p>
      <div
        className={cn(
          "p-3 pb-1.5 rounded-2xl sm:max-w-3/4",
          styles.fadeIn,
          m.who === "user"
            ? isDarkBg ? "self-end text-light-80" : "self-end text-dark-80"
            : whiteBg ? "self-start text-dark-80"
              : "self-start",
          agentPage
            ? m.who === "user" ? "bg-light-50" : "bg-light-80"
            : m.who === "user" ?
              isDarkBg ? "bg-light-10" : whiteBg ? "bg-dark-10" : "bg-white"
              : isDarkBg ? "bg-light-5" : whiteBg ? "bg-dark-5" : "bg-light-80",
          m.isStreaming && "border border-green-emerald"
        )}
      >
        {m.who === "bot"
          ? <Markdown
            className={styles.markdown}
            options={{
              overrides: {
                a: {
                  component: ({ children, ...props }) => (
                    <a
                      {...props}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {children}
                    </a>
                  ),
                },
              },
            }}
          >{m.text}</Markdown>
          : <div className={styles.markdown}><p>{m.text}</p></div>}
      </div>
      {/* Add feedback buttons for completed bot messages with workflow run ID */}
      {m.who === "bot" && !m.isStreaming && m.workflowRunId && (
        <FeedbackButtons
          message={m}
          messageIndex={i}
          onFeedback={onFeedback}
          isDarkBg={isDarkBg}
        />
      )}
    </div>
  )) : null;
});
